// frontend/packages/business-console/src/pages/Scheduling/AvailabilityTab.jsx
//
// The availability editor: staff, their weekly hours + date overrides + time
// off, and the bookable services (appointment types). Staff selection scopes
// the hours + time-off panels; services are business-wide with a staff
// multiselect. Loads the staff roster once here and passes it down so the
// panels share one source.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, Grid, Typography } from '@mui/material';

import { listStaff } from '../../services/schedulingService';
import { Surface } from '../../components/primitives';
import StaffPanel from './StaffPanel';
import WeeklyHoursEditor from './WeeklyHoursEditor';
import TimeOffPanel from './TimeOffPanel';
import AppointmentTypesPanel from './AppointmentTypesPanel';

function serverMessage(err, fallback) {
  return err?.response?.data?.message || err?.message || fallback;
}

export default function AvailabilityTab() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const loadStaff = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await listStaff();
      setStaff(rows);
      // Keep a valid selection: default to the first member, and drop a
      // selection that no longer exists (e.g. after a delete).
      setSelectedId((cur) => {
        if (cur && rows.some((m) => m._id === cur)) return cur;
        return rows[0]?._id || null;
      });
    } catch (err) {
      setError(serverMessage(err, "Couldn't load staff."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  const selectedStaff = useMemo(
    () => staff.find((m) => m._id === selectedId) || null,
    [staff, selectedId]
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {error ? (
        <Alert severity="error" onClose={() => loadStaff()}>
          {error}
        </Alert>
      ) : null}

      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <StaffPanel
            staff={staff}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onChanged={loadStaff}
          />
        </Grid>
        <Grid item xs={12} md={7}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {selectedStaff ? (
              <>
                <WeeklyHoursEditor
                  key={`hours-${selectedStaff._id}`}
                  staff={selectedStaff}
                />
                <TimeOffPanel
                  key={`timeoff-${selectedStaff._id}`}
                  staff={selectedStaff}
                />
              </>
            ) : (
              <Surface sx={{ p: 2.5 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Add a staff member to set weekly hours and time off.
                </Typography>
              </Surface>
            )}
          </Box>
        </Grid>
      </Grid>

      <AppointmentTypesPanel staff={staff} />
    </Box>
  );
}
